import { type EntityId } from '../model/ids';

/**
 * Tier-2 constraints — the relational half of ADR-0003.
 *
 * These are the constraints that genuinely *are* equation systems and cannot be
 * expressed as constructions: they relate objects bidirectionally, so there is no
 * "parent" to derive a "child" from. Asking for two existing free segments to have equal
 * length says nothing about which one should move.
 *
 * Everything the brief lists that *can* be expressed constructively is Tier 1 and is
 * deliberately absent here — `Coincident`, `Midpoint`, `On Line`, `On Circle`,
 * `On Segment` and `Locked` are all constructions, and modelling them as solver
 * constraints would trade exactness for convergence error for no gain.
 *
 * ## Constraints reference entities, not raw points
 *
 * A constraint names entities (`this segment`, `that circle`). The solver resolves each
 * to the free points that define it — a segment to its two endpoints, a circle to its
 * centre and rim point. Naming points directly would make every constraint break when a
 * segment is rebuilt from different endpoints, and it would put document structure into
 * the constraint records.
 */

declare const constraintIdBrand: unique symbol;
export type ConstraintId = string & { readonly [constraintIdBrand]: 'ConstraintId' };

export function asConstraintId(raw: string): ConstraintId {
  return raw as ConstraintId;
}

/** Which of the two tangency configurations is meant. */
export const TangentKind = {
  /** Circles touch from outside: centre distance equals the sum of the radii. */
  External: 'external',
  /** One circle touches the other from inside: centre distance equals the difference. */
  Internal: 'internal',
} as const;
export type TangentKind = (typeof TangentKind)[keyof typeof TangentKind];

interface ConstraintBase {
  readonly id: ConstraintId;
  /**
   * Relative importance. Constraints are solved in a least-squares sense, so an
   * over-constrained cluster settles on a compromise weighted by this. Left at 1 for
   * everything the user creates; the drag anchor uses a small value so it yields to real
   * constraints instead of fighting them.
   */
  readonly weight?: number;
}

/** Two points a fixed distance apart. */
export interface FixedDistance extends ConstraintBase {
  readonly kind: 'fixed-distance';
  readonly a: EntityId;
  readonly b: EntityId;
  readonly value: number;
}

/** Two linear entities of equal length. */
export interface EqualLength extends ConstraintBase {
  readonly kind: 'equal-length';
  readonly first: EntityId;
  readonly second: EntityId;
}

export interface Parallel extends ConstraintBase {
  readonly kind: 'parallel';
  readonly first: EntityId;
  readonly second: EntityId;
}

export interface Perpendicular extends ConstraintBase {
  readonly kind: 'perpendicular';
  readonly first: EntityId;
  readonly second: EntityId;
}

export interface Horizontal extends ConstraintBase {
  readonly kind: 'horizontal';
  readonly entity: EntityId;
}

export interface Vertical extends ConstraintBase {
  readonly kind: 'vertical';
  readonly entity: EntityId;
}

/** The angle `a`–`vertex`–`c`, in radians. */
export interface FixedAngle extends ConstraintBase {
  readonly kind: 'fixed-angle';
  readonly a: EntityId;
  readonly vertex: EntityId;
  readonly c: EntityId;
  readonly value: number;
}

export interface EqualAngle extends ConstraintBase {
  readonly kind: 'equal-angle';
  readonly firstA: EntityId;
  readonly firstVertex: EntityId;
  readonly firstC: EntityId;
  readonly secondA: EntityId;
  readonly secondVertex: EntityId;
  readonly secondC: EntityId;
}

/** Two circles sharing a centre. */
export interface Concentric extends ConstraintBase {
  readonly kind: 'concentric';
  readonly first: EntityId;
  readonly second: EntityId;
}

/** Two circles touching at exactly one point. */
export interface Tangent extends ConstraintBase {
  readonly kind: 'tangent';
  readonly first: EntityId;
  readonly second: EntityId;
  readonly tangentKind: TangentKind;
}

export type Constraint =
  | FixedDistance
  | EqualLength
  | Parallel
  | Perpendicular
  | Horizontal
  | Vertical
  | FixedAngle
  | EqualAngle
  | Concentric
  | Tangent;

export type ConstraintKind = Constraint['kind'];

/**
 * The entities a constraint refers to.
 *
 * Used to build the constraint graph for cluster detection, and to cascade removal when
 * an entity is deleted — a constraint whose subject is gone must go with it.
 */
export function entitiesOf(constraint: Constraint): readonly EntityId[] {
  switch (constraint.kind) {
    case 'fixed-distance':
      return [constraint.a, constraint.b];

    case 'equal-length':
    case 'parallel':
    case 'perpendicular':
    case 'concentric':
    case 'tangent':
      return [constraint.first, constraint.second];

    case 'horizontal':
    case 'vertical':
      return [constraint.entity];

    case 'fixed-angle':
      return [constraint.a, constraint.vertex, constraint.c];

    case 'equal-angle':
      return [
        constraint.firstA,
        constraint.firstVertex,
        constraint.firstC,
        constraint.secondA,
        constraint.secondVertex,
        constraint.secondC,
      ];
  }
}

/**
 * How many independent equations the constraint contributes.
 *
 * Used to report over- and under-constrained clusters to the user, which is the
 * difference between "this sketch cannot be satisfied" and a solver that silently
 * settles on a least-squares compromise nobody asked for.
 */
export function degreesRemoved(constraint: Constraint): number {
  switch (constraint.kind) {
    case 'concentric':
      return 2; // both coordinates of the centre
    case 'fixed-distance':
    case 'equal-length':
    case 'parallel':
    case 'perpendicular':
    case 'horizontal':
    case 'vertical':
    case 'fixed-angle':
    case 'equal-angle':
    case 'tangent':
      return 1;
  }
}

export function weightOf(constraint: Constraint): number {
  return constraint.weight ?? 1;
}
